// HR Action Center — "what requires HR attention right now?" Not a dashboard: each item is an
// actionable WorkforceAction (Phase 1: contract lifecycle + probation). HR-gated
// (performance:manage). Shows why the signal fired, a timeline, and resolve/acknowledge.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, AlertCircle, Clock, CheckCircle2, ChevronRight, FileText, Info } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { getApiErrorMessage } from '@/lib/apiError';
import * as api from '@/services/performanceService';

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '');
const CATEGORY_LABEL: Record<string, string> = {
  CONTRACT: 'Contracts', PROBATION: 'Probation', ATTENDANCE: 'Attendance', PERFORMANCE: 'Performance',
  ENGAGEMENT: 'Engagement', WORK: 'Work', SURVEY: 'Survey', OTHER: 'Other',
};
const RESOLUTIONS: Record<string, { value: string; label: string }[]> = {
  CONTRACT: [
    { value: 'RENEWED', label: 'Renewed' }, { value: 'EXTENDED', label: 'Extended' },
    { value: 'NON_RENEWAL', label: 'Non-renewal decided' }, { value: 'ESCALATED', label: 'Escalated' },
    { value: 'NO_ACTION', label: 'No action required' },
  ],
  PROBATION: [
    { value: 'CONFIRMED', label: 'Confirmed' }, { value: 'EXTENDED', label: 'Probation extended' },
    { value: 'NOT_CONFIRMED', label: 'Not confirmed' }, { value: 'NO_ACTION', label: 'No action required' },
  ],
  ATTENDANCE: [
    { value: 'SPOKE_WITH_EMPLOYEE', label: 'Spoke with employee' },
    { value: 'FORMAL_WARNING', label: 'Formal warning issued' },
    { value: 'EXPLAINED', label: 'Explained (approved reason)' },
    { value: 'ESCALATED', label: 'Escalated to HR' },
    { value: 'NO_ACTION', label: 'No action required' },
  ],
  PERFORMANCE: [
    { value: 'COACHING', label: 'Coaching / check-in done' },
    { value: 'TRAINING', label: 'Training arranged' },
    { value: 'PIP_STARTED', label: 'PIP started' },
    { value: 'WORKLOAD_ADJUSTED', label: 'Workload adjusted' },
    { value: 'ESCALATED', label: 'Escalated to HR' },
    { value: 'NO_ACTION', label: 'No action required' },
  ],
  ENGAGEMENT: [
    { value: 'CHECKIN_DONE', label: 'Check-in completed' },
    { value: 'FOLLOWUP_SCHEDULED', label: 'Follow-up scheduled' },
    { value: 'ESCALATED', label: 'Escalated to HR' },
    { value: 'NO_ACTION', label: 'No action required' },
  ],
};
const defaultResolutions = [{ value: 'DONE', label: 'Resolved' }, { value: 'NO_ACTION', label: 'No action required' }];

export const HRActionCenter: React.FC = () => {
  const [actions, setActions] = useState<api.WorkforceAction[]>([]);
  const [summary, setSummary] = useState<api.HRActionCategoryCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState<string>('');
  const [sel, setSel] = useState<api.WorkforceAction | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [resType, setResType] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.listHRActions(cat || undefined, true)
    .then((r) => { setActions(r.actions || []); setSummary(r.summary || []); })
    .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load HR actions')))
    .finally(() => setLoading(false));
  useEffect(() => { setLoading(true); void load(); /* eslint-disable-next-line */ }, [cat]);

  const totalOpen = useMemo(() => summary.reduce((a, c) => a + c.open, 0), [summary]);
  const open = async (a: api.WorkforceAction) => {
    setSel(a); setEvents([]); setResType(''); setNote('');
    try { const d = await api.getHRAction(a.id); setEvents(d.events || []); } catch { /* ignore */ }
  };
  const guard = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setSel(null); await load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Something went wrong')); }
    finally { setBusy(false); }
  };
  const resolutions = sel ? (RESOLUTIONS[sel.category] || defaultResolutions) : defaultResolutions;

  if (loading) return <div className="p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="max-w-5xl space-y-4 p-1">
      <div>
        <h1 className="text-2xl font-bold text-foreground">HR Action Center</h1>
        <p className="text-sm text-muted-foreground">What requires HR attention right now.</p>
      </div>

      {/* Requires Attention summary */}
      <Card><CardContent className="pt-4">
        <div className="mb-3 flex items-baseline justify-between">
          <span className="text-sm font-semibold text-foreground">Requires attention</span>
          <span className="text-2xl font-bold text-primary-500">{totalOpen}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterChip label="All" active={cat === ''} onClick={() => setCat('')} />
          {summary.map((c) => (
            <FilterChip key={c.category} active={cat === c.category} onClick={() => setCat(c.category)}
              label={`${CATEGORY_LABEL[c.category] || c.category} ${c.open}`} overdue={c.overdue} />
          ))}
        </div>
      </CardContent></Card>

      {actions.length === 0 && (
        <Card><CardContent className="flex items-center gap-2 pt-4 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-green-600" />All caught up — nothing needs attention.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {actions.map((a) => {
          const overdue = a.due_at && new Date(a.due_at) < new Date();
          return (
            <button key={a.id} type="button" onClick={() => open(a)}
              className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${overdue ? 'bg-red-500/10 text-red-600' : 'bg-primary-500/10 text-primary-500'}`}>
                {overdue ? <AlertCircle className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-foreground">{a.title}</span>
                  <Badge className="border-0 bg-muted text-[10px] text-muted-foreground">{CATEGORY_LABEL[a.category] || a.category}</Badge>
                  {a.status !== 'New' && <Badge className="border-0 bg-muted text-[10px] text-muted-foreground">{a.status}</Badge>}
                </div>
                <div className="truncate text-xs text-muted-foreground">{a.employee_name}{a.due_at ? ` · due ${fmtDate(a.due_at)}` : ''}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          );
        })}
      </div>

      {/* Detail drawer */}
      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setSel(null)}>
          <div className="h-full w-full max-w-md overflow-y-auto border-l border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-start justify-between gap-2">
              <h2 className="text-lg font-bold text-foreground">{sel.title}</h2>
              <button type="button" onClick={() => setSel(null)} className="text-sm text-muted-foreground hover:text-foreground">Close</button>
            </div>
            <p className="text-sm text-muted-foreground">{sel.employee_name}</p>

            <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><Info className="h-3.5 w-3.5" />Why you’re seeing this</div>
              <p className="text-sm text-foreground">{sel.summary}</p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span>Source: {sel.source_domain}</span>
                <span>Severity: {sel.severity}</span>
                {sel.due_at && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Due {fmtDate(sel.due_at)}</span>}
              </div>
            </div>

            {sel.status !== 'Resolved' && (
              <div className="mt-4 space-y-3">
                {sel.status === 'New' && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => guard(() => api.acknowledgeHRAction(sel.id), 'Acknowledged')}>Acknowledge</Button>
                )}
                <div className="rounded-lg border border-border p-3">
                  <label className="text-xs font-semibold text-muted-foreground">Record outcome</label>
                  <select value={resType} onChange={(e) => setResType(e.target.value)} className="mt-1.5 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm">
                    <option value="">Choose a resolution…</option>
                    {resolutions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" rows={2}
                    className="mt-2 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" />
                  <Button size="sm" className="mt-2" disabled={busy || !resType} onClick={() => guard(() => api.resolveHRAction(sel.id, resType, note.trim() || undefined), 'Resolved')}>
                    Resolve
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-4">
              <div className="mb-1.5 text-xs font-semibold text-muted-foreground">Timeline</div>
              <ul className="space-y-1.5">
                {events.map((ev, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className={`h-1.5 w-1.5 rounded-full ${ev.actor_source === 'agent' ? 'bg-purple-500' : ev.actor_source === 'user' ? 'bg-primary-500' : 'bg-muted-foreground'}`} />
                    <span className="text-foreground">{String(ev.kind)}</span>
                    <span>· {ev.actor_source === 'agent' ? 'Dana' : ev.actor_source === 'user' ? 'HR' : 'system'}</span>
                    <span>· {ev.at ? new Date(ev.at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FilterChip: React.FC<{ label: string; active: boolean; overdue?: number; onClick: () => void }> = ({ label, active, overdue, onClick }) => (
  <button type="button" onClick={onClick}
    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${active ? 'border-primary-400 bg-accent text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`}>
    {label}{overdue ? <span className="ml-1 text-red-600">· {overdue} overdue</span> : ''}
  </button>
);
